import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Plus } from 'lucide-react';
import { useMyJournalEntries } from '@/hooks/useJournalApi';
import { toLocalDateStr, todayLocalDateStr, fromLocalDateStr } from '@/lib/date';

interface JournalCalendarProps {
    selectedDate: string;
    onDateSelect: (date: string) => void;
    onCreateEntry?: (date: string) => void;
}

export const JournalCalendar: React.FC<JournalCalendarProps> = ({
    selectedDate,
    onDateSelect,
    onCreateEntry,
}) => {
    const [currentMonth, setCurrentMonth] = useState(() => {
        const date = fromLocalDateStr(selectedDate);
        return new Date(date.getFullYear(), date.getMonth(), 1);
    });

    // Fetch journal entries for the current month
    const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

    const { data: monthEntries = [] } = useMyJournalEntries({
        startDate: toLocalDateStr(startOfMonth),
        endDate: toLocalDateStr(endOfMonth),
    });

    // Create a map of dates to entries for quick lookup
    const entriesMap = useMemo(() => {
        const map = new Map<string, any[]>();
        monthEntries.forEach((entry) => {
            // Stays UTC on purpose. entry.date is the day the entry is *for*,
            // which the backend stores as midnight UTC, so reading it back in
            // UTC returns that same day. Reading it in local time would roll it
            // back a day for users behind UTC.
            const entryDate = new Date(entry.date).toISOString().split('T')[0];
            if (!map.has(entryDate)) {
                map.set(entryDate, []);
            }
            map.get(entryDate)!.push(entry);
        });
        return map;
    }, [monthEntries]);

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentMonth((prev) => {
            const newMonth = new Date(prev);
            if (direction === 'prev') {
                newMonth.setMonth(prev.getMonth() - 1);
            } else {
                newMonth.setMonth(prev.getMonth() + 1);
            }
            return newMonth;
        });
    };

    const getDaysInMonth = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push(null);
        }

        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateString = toLocalDateStr(date);
            days.push({
                date: day,
                dateString,
                isToday: dateString === todayLocalDateStr(),
                isSelected: dateString === selectedDate,
                entries: entriesMap.get(dateString) || [],
            });
        }

        return days;
    };

    const formatMonthYear = () => {
        return currentMonth.toLocaleDateString('en-US', {
            month: 'long',
            year: 'numeric',
        });
    };

    const getEntryStatus = (entries: any[]) => {
        if (entries.length === 0) return 'none';
        if (entries.some((entry) => entry.isCompleted)) return 'completed';
        return 'draft';
    };

    // pointsEarned is the whole day's points, derived server-side from that
    // day's meal logs (#223) — not this entry's own share. Entries are unique
    // per template per date, so a day with two templates would carry the same
    // figure twice; take it once rather than adding them up.
    const getTotalPoints = (entries: any[]) => {
        return entries[0]?.pointsEarned || 0;
    };

    const days = getDaysInMonth();
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <Card className="p-6">
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                    <CalendarIcon className="w-5 h-5 text-brand-text-blue" />
                    <h3 className="text-lg font-semibold text-foreground">Journal Calendar</h3>
                </div>
                <div className="flex items-center space-x-2">
                    <Button variant="outline" size="sm" onClick={() => navigateMonth('prev')}>
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-[140px] text-center">
                        {formatMonthYear()}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigateMonth('next')}>
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Day Labels */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {dayLabels.map((label) => (
                    <div
                        key={label}
                        className="p-2 text-xs font-medium text-muted-foreground text-center"
                    >
                        {label}
                    </div>
                ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((day, index) => {
                    if (!day) {
                        return <div key={index} className="p-2 h-20" />;
                    }

                    const entryStatus = getEntryStatus(day.entries);
                    const totalPoints = getTotalPoints(day.entries);

                    return (
                        <div
                            key={day.dateString}
                            className={`
                p-2 h-20 border rounded-lg cursor-pointer transition-all duration-200
                hover:bg-muted hover:border-border relative
                ${day.isSelected ? 'bg-brand-header-wash border-brand-header ring-2 ring-brand-header' : 'border-border'}
                ${day.isToday ? 'ring-2 ring-brand-text-green border-brand-text-green' : ''}
                ${entryStatus !== 'none' ? 'bg-brand-header-wash border-border' : ''}
                ${entryStatus === 'completed' ? 'bg-brand-fill-green border-brand-text-green' : ''}
              `}
                            onClick={() => onDateSelect(day.dateString)}
                        >
                            <div className="flex items-start justify-between h-full">
                                <div className="flex flex-col h-full">
                                    <div className="flex items-center space-x-1">
                                        <span
                                            className={`text-sm ${
                                                day.isToday
                                                    ? 'font-bold text-foreground underline'
                                                    : 'font-medium text-foreground'
                                            }`}
                                        >
                                            {day.date}
                                        </span>
                                        {entryStatus !== 'none' && (
                                            <div
                                                className={`w-2 h-2 rounded-full ${
                                                    entryStatus === 'completed'
                                                        ? 'bg-brand-fill-ink'
                                                        : 'bg-brand-text-green'
                                                }`}
                                            ></div>
                                        )}
                                    </div>

                                    {/* Entry indicators */}
                                    <div className="flex-1 flex flex-col justify-between mt-1">
                                        {entryStatus !== 'none' && (
                                            <div className="space-y-1">
                                                <Badge
                                                    variant={
                                                        entryStatus === 'completed'
                                                            ? 'default'
                                                            : 'secondary'
                                                    }
                                                    className="text-xs px-1 py-0 h-4"
                                                >
                                                    {day.entries.length}{' '}
                                                    {day.entries.length === 1 ? 'entry' : 'entries'}
                                                </Badge>
                                                {totalPoints > 0 && (
                                                    <div className="text-xs text-foreground font-medium">
                                                        {totalPoints} pts
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {entryStatus === 'none' && onCreateEntry && (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onCreateEntry(day.dateString);
                                                }}
                                            >
                                                <Plus className="w-3 h-3" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 bg-brand-header-wash rounded border-2 border-brand-header"></div>
                            <span>Today</span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <Badge variant="default" className="h-4 text-xs">
                                entry
                            </Badge>
                            <span>Completed</span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <Badge variant="secondary" className="h-4 text-xs">
                                entry
                            </Badge>
                            <span>Draft</span>
                        </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Click a date to view or create entries
                    </div>
                </div>
            </div>
        </Card>
    );
};
